import React, { useEffect, useState } from 'react';
import { Routes, Route, useParams, useLocation } from 'react-router-dom';
import { RegisterPage } from './pages/RegisterPage';
import { VerifyPage } from './pages/VerifyPage';
import { ProtectedPage } from './pages/ProtectedPage';
import formbricks from "@formbricks/js";

export default function App() {
  const location = useLocation();
  return (
    <Routes location={location}>
      <Route path=":panoId?" element={<MainContent key={location.pathname} />} />
    </Routes>
  );
}

function MainContent() {
  const { panoId: routePanoId } = useParams<{ panoId?: string }>();
  const [step, setStep] = useState<'loading'|'register'|'verify'|'protected'>('loading');
  const [email, setEmail] = useState<string>('');
  const [jwt, setJwt] = useState<string>('');
  const [panoId, setPanoId] = useState<string>('1');
  const [key, setKey] = useState<string>('');
  const [token, setToken] = useState<string>('');
  const [dashboardId, setDashboardId] = useState<string>('');
  const [error, setError] = useState<string>('');

  // Track if Formbricks has been initialized to prevent double init
  const [formbricksInitialized, setFormbricksInitialized] = useState(false);
  
  const location = useLocation();

  useEffect(() => {
    let pid = '139';
    // if (routePanoId && parseInt(routePanoId, 10) > 0 && parseInt(routePanoId, 10) < 500 ) {
    //   pid = routePanoId;
    // }
    setPanoId(pid);

    const storedEmail = localStorage.getItem('email') || '';
    const storedJwt = localStorage.getItem('jwt') || '';

    if (storedEmail && storedJwt) {
      // Attempt to fetch data from the new showcase/item endpoint using JWT
      (async () => {
        try {
          const resp = await fetch('https://rpa.dataritim.com/webhook/core/item', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${storedJwt}`
            },
            body: JSON.stringify({ email: storedEmail, panoId: pid })
          });
          if (resp.ok) {
            const data = await resp.json();
            setEmail(storedEmail);
            setJwt(storedJwt);
            setDashboardId(data.dashboardId);
            setKey(data.key);
            setToken(data.token);
            setStep('protected');
          } else if (resp.status === 401) {
            // If unauthorized, logout from Formbricks and reset local storage
            formbricks.logout();
            localStorage.removeItem('jwt');
            localStorage.removeItem('email');
            setStep('register');
          } else {
            setStep('register');
          }
        } catch {
          setStep('register');
        }
      })();
    } else {
      // No stored credentials, move to register
      setTimeout(() => setStep('register'), 500);
    }
  }, [routePanoId]);

  // Initialize Formbricks once the user is verified and on protected step
  useEffect(() => {
    if (step === 'protected' && !formbricksInitialized) {
      // Initialize Formbricks with user info
      formbricks.init({
        environmentId: "cmdiqqh1t1nbg2x02txwr2il3",
        apiHost: "https://form.dataritim.com",
        userId: email, // userId is the email
        attributes: {
          panoId: panoId,
          jwt: jwt
        },
      });
      
      // Reset Formbricks to ensure a fresh state on page reload
      formbricks.reset();
      
      // Register route changes so Formbricks can trigger surveys on URL changes if configured
      formbricks.registerRouteChange();

      setFormbricksInitialized(true);
    }
  }, [step, formbricksInitialized, email, panoId, jwt]);

  // Check for URL triggers. If URL has ?triggerForm=survey123, for example, trigger a survey.
  // This won't reload dashboard or chat, just triggers an action in Formbricks.
  useEffect(() => {
    if (formbricksInitialized) {
      const params = new URLSearchParams(window.location.search);
      const triggerForm = params.get('triggerForm');
      if (triggerForm) {
        // Track a custom action to trigger the survey. 
        // Make sure to configure a corresponding action in Formbricks dashboard.
        formbricks.track(`Trigger Survey ${triggerForm}`);
      }
    }
  }, [location.search, formbricksInitialized]);

  const handleRegister = async (inputEmail: string) => {
    setError('');
    try {
      const resp = await fetch('https://rpa.dataritim.com/webhook/core/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inputEmail })
      });
      if (resp.ok) {
        setEmail(inputEmail);
        localStorage.setItem('email', inputEmail);
        setStep('verify');
      } else {
        const errorText = await resp.text();
        setError(errorText || 'Giriş başarısız, lütfen tekrar deneyin.');
      }
    } catch {
      setError('Giriş başarısız, lütfen tekrar deneyin.');
    }
  };

  const handleVerify = async (code: string) => {
    setError('');
    try {
      const resp = await fetch('https://rpa.dataritim.com/webhook/core/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, panoId, code })
      });
      if (resp.ok) {
        const data = await resp.json();
        // data contains: jwt, dashboardId, key, token
        setKey(data.key);
        setToken(data.token);
        setDashboardId(data.dashboardId);
        setJwt(data.jwt);
        localStorage.setItem('jwt', data.jwt);
        setStep('protected');
      } else {
        const errorText = await resp.text();
        setError(errorText || 'Doğrulama başarısız, lütfen tekrar deneyin.');
      }
    } catch {
      setError('Doğrulama başarısız, lütfen tekrar deneyin.');
    }
  };

  if (step === 'loading') {
    return (
      <div className="full-screen-loader">
        <div className="spinner"></div>
      </div>
    );
  }

  if (step === 'register') {
    return <RegisterPage onSubmit={handleRegister} error={error} />;
  }

  if (step === 'verify') {
    return <VerifyPage email={email} onSubmit={handleVerify} error={error} />;
  }

  if (step === 'protected') {
    return (
      <ProtectedPage
        dashboardId={dashboardId}
        authKey={key}
        authToken={token}
        panoId={panoId}
        email={email}
        jwt={jwt}
      />
    );
  }

  return null;
}
